import { useMemo } from 'react';
import { useAuth } from './useAuth';
import { useTenant } from './useTenant';
import { rolePermissions, UserRole, PermissionAction } from '@/types/roles';
import { logger } from '../lib/logger';

const permissionActions: PermissionAction[] = ['read', 'create', 'update', 'delete', 'download'];

export function usePermissions() {
  const { user } = useAuth();
  const { hasModule, isLoading: tenantLoading } = useTenant();

  const permissions = useMemo(() => {
    if (!user?.role) return null;
    if (import.meta.env.DEV && !(user.role in rolePermissions)) {
      logger.warn(`[usePermissions] rol desconocido: ${user.role}`);
    }
    return rolePermissions[user.role];
  }, [user?.role]);

  const can = (module: string, action: string) => {
    if (!permissions) return false;

    const modulePermissions = permissions[module];
    if (!modulePermissions || !permissionActions.includes(action as PermissionAction)) {
      return false;
    }

    return modulePermissions[action as PermissionAction];
  };

  const hasPermission = can;

  const hasRole = (...roles: UserRole[]) => {
    if (!user?.role) return false;
    return roles.includes(user.role as UserRole);
  };

  const isSuperAdmin = () => {
    return hasRole('super_admin');
  };

  const isAdmin = () => {
    return hasRole('administrador_gerencial', 'super_admin');
  };

  const isReceptionist = () => {
    return hasRole('administrador_recepcionista');
  };

  const isProfessional = () => {
    return hasRole('profesional');
  };

  const canAccessModule = (module: string) => {
    if (!can(module, 'read')) {
      return false;
    }

    if (user?.role === 'super_admin') {
      return true;
    }

    if (tenantLoading) {
      return false;
    }

    return hasModule(module);
  };

  return {
    permissions,
    can,
    hasPermission,
    hasRole,
    isSuperAdmin,
    isAdmin,
    isReceptionist,
    isProfessional,
    canAccessModule,
    tenantLoading,
    role: user?.role as UserRole | undefined,
  };
}
